import { useState } from "react";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import { toast } from "react-toastify";
import actionsHandler from "@/store/actions/actionHandler";
import { useFormik } from "formik";
import * as Yup from "yup";

interface AddClientProps {
  onClose: () => void;
  onSubmit: () => void;
  isModalOpen: boolean;
}

const AddClient = ({ onClose, onSubmit, isModalOpen }: AddClientProps) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const [isLoading, setIsLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .required("Email is required")
        .email("Invalid email format. Please check and try again"),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        setIsLoading(true);
        const { authenticatedCall } = actionsHandler("inviteUser");
        const response = await dispatch(
          authenticatedCall({
            payload: {
              email: values.email,
              user_type:
                JSON.parse(localStorage.getItem("user") || "{}")?.user_type
                  ?.id + 1, // Client type
            },
          }),
        );

        if (response.ok) {
          toast.success(
            response.data?.message || "Client invited successfully",
          );
          resetForm();
          onSubmit();
        }
      } catch (error) {
        console.error("Error:", error);
        toast.error("Unable to invite client. Please try again later");
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  if (!isModalOpen) return null;

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-black/25 tw-flex tw-items-center tw-justify-center tw-z-50">
      <div className="tw-bg-white tw-rounded-2xl tw-w-96 tw-p-6">
        <form onSubmit={formik.handleSubmit}>
          <div className="tw-flex tw-justify-between tw-items-center tw-mb-6">
            <h2 className="tw-text-base font-weight-semibold font-primary-color">
              Enter the email address of the client you'd like to add.
            </h2>
          </div>

          <div className="form-group tw-mb-6">
            <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
              Email
            </p>
            <input
              type="email"
              name="email"
              placeholder="Enter email address"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={`tw-w-full tw-border ${
                formik.touched.email && formik.errors.email
                  ? "tw-border-red-500"
                  : "tw-border-gray-300"
              } tw-rounded-md tw-p-2`}
            />
            {formik.touched.email && formik.errors.email && (
              <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                {formik.errors.email}
              </div>
            )}
          </div>
          <div className="tw-flex tw-space-x-4">
            <button
              type="button"
              onClick={handleClose}
              className="tw-flex-1 tw-px-4 tw-py-2 tw-border font-default-primary-color tw-border-[#146C94] tw-rounded-lg tw-w-[50%] tw-text-center"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !formik.isValid}
              className="tw-bg-blue-600 hover:tw-bg-blue-700 tw-flex tw-items-center tw-gap-2 tw-w-[50%] tw-justify-center tw-text-white tw-rounded-md tw-px-4 tw-py-2 primary-custom-button disabled:tw-opacity-50"
            >
              {isLoading ? "Adding..." : "Add"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddClient;
